import { Component, inject, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FormWrapperComponent } from '../../../ui/form-wrapper/form-wrapper.component';
import { InputFieldComponent } from '../../../ui/inputField/inputField.component';
import { FormBuilder, FormArray, ReactiveFormsModule, Validators } from '@angular/forms';
import { CampaignStepperService } from '../../../../services/campaign/campaign-stepper.service';
import { ProcessCampaignService } from '../../../../services/campaign/process-campaign.service';
import { ScheduleService } from '../../../../services/campaign/schedule.service';
import { MessagesService, SavedMessages } from '../../../../services/campaign/messages.service';
import { UtilityService } from '../../../../services/utility.service';
import { CancelCampaignService } from '../../../../services/campaign/cancel-campaign.service';
import { TimeZoneService } from '../../../../services/timezone/time-zone.service';
import {
  browserTimeZone,
  scheduleMoment,
} from '../../../../pages/organization/campaigns/campaign-list/campaign-facts';

@Component({
  selector: 'app-schedule-form',
  standalone: true,
  imports: [FormWrapperComponent, InputFieldComponent, MatTooltipModule, ReactiveFormsModule],
  templateUrl: './schedule-form.component.html',
  styleUrl: '../campaign-form.css',
})
export class ScheduleFormComponent implements OnDestroy {
  campaignStepperService = inject(CampaignStepperService);
  processCampaignService = inject(ProcessCampaignService);
  scheduleService = inject(ScheduleService);
  messagesService = inject(MessagesService);
  cancelCampaignService = inject(CancelCampaignService);
  utility = inject(UtilityService);
  timeZoneService = inject(TimeZoneService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);

  /** New send times start in the browser's zone, which joins the list if it is missing. */
  readonly defaultZone = browserTimeZone();
  private readonly zones = (() => {
    const list = this.timeZoneService.getTimeZones();
    return list.some((tz) => tz.timezone === this.defaultZone)
      ? list
      : [{ timezone: this.defaultZone, offset: '' }, ...list];
  })();

  get getTimeZones() {
    return this.zones;
  }

  // timeout ID for cleanup
  private scheduleTimeoutId?: ReturnType<typeof setTimeout>;

  constructor(private fb: FormBuilder) {}

  /** Today in the browser's calendar, as the date input expects it. */
  today = (() => {
    const now = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
  })();

  campaignSchedule = this.fb.group({
    campaignStart: ['', Validators.required],
    campaignEnd: ['', Validators.required],
  });

  messageSchedule = this.newMessageSchedule();

  get campaignStart() {
    return this.campaignSchedule.get('campaignStart');
  }

  get campaignEnd() {
    return this.campaignSchedule.get('campaignEnd');
  }

  get endDateSet(): boolean {
    return this.campaignSchedule.controls.campaignEnd.valid;
  }

  get startDateSet(): boolean {
    return this.campaignSchedule.controls.campaignStart.valid;
  }

  get messageDates() {
    return this.messageSchedule.get('messageDates') as FormArray;
  }

  /** Saved messages that have no send time yet. */
  get unscheduledMessages(): SavedMessages[] {
    return this.messagesService.savedMessages.filter(
      (message) =>
        !this.scheduleService.messageSchedule.some(
          (schedule) => schedule.message?.id === message.id && schedule.messageDates.length > 0
        )
    );
  }

  /** Continue needs the dates and a send time for every message. */
  get blocked(): boolean {
    return (
      this.campaignSchedule.invalid ||
      this.scheduleService.messageSchedule.length < 1 ||
      this.unscheduledMessages.length > 0
    );
  }

  /** " · 2 times" after a message that already has send times. */
  scheduledLabel(messageId: string): string {
    const count = this.scheduleService.messageSchedule
      .filter((schedule) => schedule.message?.id === messageId)
      .reduce((sum, schedule) => sum + schedule.messageDates.length, 0);
    if (!count) return '';
    return count === 1 ? ' · 1 time' : ` · ${count} times`;
  }

  private newDateRow() {
    return this.fb.group({
      date: ['', Validators.required],
      time: ['', Validators.required],
      timezone: [this.defaultZone, Validators.required],
    });
  }

  private newMessageSchedule() {
    return this.fb.group({
      message: ['', Validators.required],
      messageDates: this.fb.array([this.newDateRow()]),
    });
  }

  addMessageDates() {
    this.messageDates.push(this.newDateRow());
  }

  removeMessageDates(index: number) {
    this.messageDates.removeAt(index);
  }

  scheduleSubmitted = false; //for controlling errors on schedule message form
  saveSchedule() {
    this.scheduleSubmitted = true; // for controlling errors on form
    if (this.messageSchedule.valid) {
      // get message from message service with message id
      let message = this.messagesService.savedMessages.find(
        (item) => item.id == this.messageSchedule.value.message
      );

      // check if message already exist in schedule service and return index
      const index = this.scheduleService.messageSchedule.findIndex(
        (messageObj) => messageObj.message?.id == message?.id
      );

      // if index exists, add the new dates to that message, otherwise add a new entry
      if (index !== -1) {
        const updatedMessage = {
          ...this.scheduleService.messageSchedule[index],
          messageDates: [...this.scheduleService.messageSchedule[index].messageDates, ...this.messageDates.value],
        };

        this.scheduleService.messageSchedule[index] = updatedMessage;
      } else {
        this.scheduleService.messageSchedule.push({
          id: this.utility.randomId(),
          message,
          messageDates: this.messageDates.value,
        });
      }

      // reset input fields
      this.scheduleSubmitted = false;
      this.messageSchedule = this.newMessageSchedule();
    }
    this.validateForm();

    // to remove errors
    if (this.scheduleTimeoutId) {
      clearTimeout(this.scheduleTimeoutId);
    }
    this.scheduleTimeoutId = setTimeout(() => {
      this.scheduleSubmitted = false;
      this.scheduleTimeoutId = undefined;
    }, 5000);
  }

  invalid = true;
  validateForm() {
    this.invalid = this.scheduleService.messageSchedule.length < 1 || this.unscheduledMessages.length > 0;
  }

  submitted = false;
  handleSubmit() {
    this.submitted = true;
    this.validateForm();
    // Both must hold: dates set, and every message has a send time.
    if (this.invalid || this.campaignSchedule.invalid) {
      return;
    }
    this.campaignStepperService.stepCompleted('schedule');
    this.campaignStepperService.moveToNextStep('schedule');
  }

  goBack() {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { step: 'messages' },
      queryParamsHandling: 'merge',
    });
  }

  // validations
  validateStartDate() {
    if (this.campaignStart?.value) {
      const inputDate = new Date(this.campaignStart.value);
      const minDate = new Date(this.today);
      if (inputDate < minDate) {
        this.campaignStart?.setErrors({ minDate: true });
      } else {
        this.campaignStart?.setErrors(null);
      }
    }
    this.validateEndDate();
  }

  validateEndDate() {
    if (this.campaignEnd?.value) {
      const inputDate = new Date(this.campaignEnd.value);
      const minDate = new Date(this.campaignStart?.value ? this.campaignStart?.value : this.today);
      if (inputDate < minDate) {
        this.campaignEnd?.setErrors({ minDate: true });
      } else {
        this.campaignEnd?.setErrors(null);
      }
    }
  }

  validateMessageDate(index: number) {
    if (this.campaignStart?.value && this.campaignEnd?.value) {
      const inputControl = this.messageDates.at(index).get('date');
      if (inputControl?.value) {
        const startDate = new Date(this.campaignStart.value);
        const endDate = new Date(this.campaignEnd.value);
        const inputDate = new Date(inputControl.value);
        if (startDate <= inputDate && inputDate <= endDate) {
          inputControl?.setErrors(null);
        } else {
          inputControl?.setErrors({ outOfRange: true });
        }
      }
    }
  }

  /** A send time that has already passed, in its own time zone, cannot be saved. */
  validateMessageTime(index: number) {
    const row = this.messageDates.at(index);
    const date = row.get('date')?.value;
    const time = row.get('time');
    if (!date || !time?.value) return;

    const at = scheduleMoment(date, time.value, row.get('timezone')?.value);
    const { past, ...others } = time.errors ?? {};
    if (at && at.getTime() <= Date.now()) {
      time.setErrors({ ...others, past: true });
    } else if (past) {
      time.setErrors(Object.keys(others).length ? others : null);
    }
  }

  ngOnInit() {
    this.campaignSchedule.patchValue({
      campaignStart: this.scheduleService.campaignSchedule.campaignStart,
      campaignEnd: this.scheduleService.campaignSchedule.campaignEnd,
    });

    // Send times for messages deleted in the previous step would go nowhere.
    const ids = new Set(this.messagesService.savedMessages.map((message) => message.id));
    this.scheduleService.messageSchedule = this.scheduleService.messageSchedule.filter(
      (schedule) => !!schedule.message && ids.has(schedule.message.id)
    );

    this.validateForm();
  }

  ngOnDestroy(): void {
    // Clear timeout when component is destroyed
    if (this.scheduleTimeoutId) {
      clearTimeout(this.scheduleTimeoutId);
    }
  }
}
